import { type AccountContext, type IncidentDetailResponse, NOT_CRAWLER_SCOPED } from '@prerender/diagnostics-api';
import { useId } from 'react';

import { accountNameLabel, planLabel } from './accountLabels';
import { crawlerCategoryLabel } from './crawlerCategoryLabels';
import DetailField from './DetailField';
import { formatDateWithAge } from './formatDateWithAge';
import IncidentStatusTag from './IncidentStatusTag';
import { incidentTypeLabel } from './incidentTypeLabels';
import SeverityTag from './SeverityTag';
import UserIdChip from './UserIdChip';

type IncidentDetailRailProps = {
  incident: IncidentDetailResponse;
  now: Date;
};

type IncidentDetailRailWithActionsProps = IncidentDetailRailProps & {
  actions: React.ReactNode;
};

const NEVER_RE_CHECKED = 'not re-checked yet';

type RailSectionProps = {
  label: string;
  children: React.ReactNode;
};

function RailSection({ label, children }: RailSectionProps) {
  const labelId = useId();

  return (
    <section className="diagnostics-detail-group" aria-labelledby={labelId}>
      <h2 id={labelId} className="diagnostics-detail-label">
        {label}
      </h2>
      {children}
    </section>
  );
}

function IdentitySummary({ incident }: { incident: IncidentDetailResponse }) {
  const referenceId = useId();

  return (
    <section className="diagnostics-detail-identity" aria-labelledby={referenceId}>
      <h2 id={referenceId} className="diagnostics-detail-reference">
        {incident.reference}
      </h2>
      <SeverityTag severity={incident.severity} />
      <IncidentStatusTag status={incident.status} />
      <span className="diagnostics-detail-type">{incidentTypeLabel(incident.incidentType)}</span>
      {incident.crawlerCategory !== NOT_CRAWLER_SCOPED && (
        <span className="diagnostics-detail-crawler">{crawlerCategoryLabel(incident.crawlerCategory)}</span>
      )}
    </section>
  );
}

function ImpactSummary({ incident }: { incident: IncidentDetailResponse }) {
  return (
    <RailSection label="Impact">
      <dl className="diagnostics-detail-fields">
        <DetailField name="Affected URLs" emphasis="figure">
          {incident.affectedUrlCount}
        </DetailField>
      </dl>
    </RailSection>
  );
}

function AccountSummary({ account, userId }: { account: AccountContext | null; userId: number }) {
  return (
    <RailSection label="Account">
      <dl className="diagnostics-detail-fields">
        <DetailField name="Account" emphasis="lead">
          {accountNameLabel(account)}
        </DetailField>
        <div className="diagnostics-detail-account-line">
          <DetailField name="Plan" emphasis="quiet">
            {planLabel(account)}
          </DetailField>
        </div>
      </dl>
      <UserIdChip userId={userId} />
    </RailSection>
  );
}

function SiteSummary({ incident, now }: IncidentDetailRailProps) {
  const [worst] = incident.domains;

  return (
    <RailSection label="Site">
      {worst !== undefined && (
        <p className="diagnostics-detail-domain">
          <a href={`https://${worst.domain}`} target="_blank" rel="noreferrer">
            {worst.domain}
          </a>
        </p>
      )}
      <dl className="diagnostics-detail-fields">
        <DetailField name="Detected">{formatDateWithAge(new Date(incident.firstDetectedAt), now)}</DetailField>
        <DetailField name="Last updated">{formatDateWithAge(new Date(incident.lastUpdatedAt), now)}</DetailField>
        <DetailField name="Last checked">
          {incident.lastCheckedAt === null ? NEVER_RE_CHECKED : formatDateWithAge(new Date(incident.lastCheckedAt), now)}
        </DetailField>
        {incident.closedAt !== null && (
          <DetailField name="Closed">{formatDateWithAge(new Date(incident.closedAt), now)}</DetailField>
        )}
      </dl>
    </RailSection>
  );
}

function IncidentDetailRail({ incident, now, actions }: IncidentDetailRailWithActionsProps) {
  return (
    <aside className="diagnostics-detail-rail">
      <IdentitySummary incident={incident} />

      <ImpactSummary incident={incident} />

      <AccountSummary account={incident.account} userId={incident.userId} />

      <SiteSummary incident={incident} now={now} />

      <div className="diagnostics-detail-actions">{actions}</div>
    </aside>
  );
}

export default IncidentDetailRail;
